import { defineStore } from 'pinia';
import { computed, reactive, ref, toRef, toRefs } from 'vue';
import { watchImmediate } from '@vueuse/core';
import { assert } from '@/util/assert';
import { taggedLogger } from '@/util/taggedLogger';
import { formatLength } from '@/util/format/formatLength';
import type { Url } from '@/domain/types';
import { anatomicAngles } from '@/domain/anatomy/pelvis/acetabularAngles';
import { toRadians } from '@/domain/anatomy/pelvis/anteversionInclination';
import {
    type Adjustments,
    plannerState,
    type PlannerState,
    resetPlannerState,
} from '@/views/3d-planner/store/plannerState';
import type { PlannerContext, PlannerStore } from '@/views/3d-planner/store/plannerStore.types';
import { loadCase, reloadTemplate } from '@/views/3d-planner/operations/loading';
import { computeFittedStem } from '@/views/3d-planner/fitting/fittedStem';
import { useTemplateSyncStore } from '@/views/3d-planner/template/templateSyncStore';
import { putTemplate } from '@/api/template/putTemplate';
import { approvePlan } from '@/views/3d-planner/operations/approvePlan';
import { computeFittedCup } from '@/views/3d-planner/fitting/fittedCup';
import {
    computeStemTransformFromResectionAlignment,
    hasStemAdjustment,
} from '@/views/3d-planner/fitting/stemTransform';
import { stemChangeAnchor } from '@/views/3d-planner/fitting/stemChangeAnchor';
import {
    computeBearingUrl,
    computeHeadUrl,
    computeLinerUrl,
} from '@/views/3d-planner/fitting/componentUrls';
import type { CatalogCup } from '@/domain/catalog/cup';
import { plannerOperationExecutor } from '@/views/3d-planner/operations/executeOperation';
import { syncTemplate } from '@/views/3d-planner/template/manualTemplateState';

export type { PlannerStore } from '@/views/3d-planner/store/plannerStore.types';

const log = taggedLogger('planner');

/** Shared identity matrix for non-mutating equality checks against `stemTransform`. */

// ─── Mutations ──────────────────────────────────────────────────────────────

/** Long-running case operations gated through the operation executor. */
export function caseFlowActions(ctx: PlannerContext) {
    const { state, executor } = ctx;
    return {
        loadCase: async (caseId: number): Promise<void> => {
            executor.abortOperation();
            await executor.executeOperation('load-case', async (signal) => {
                resetPlannerState(state);
                await loadCase(state, caseId, { signal });
            });
        },
        run: () => (state._sync = true),
        stop: () => {
            executor.abortOperation();
            state._sync = false;
        },
        setTargetAdjustments: async (adjustments: Adjustments): Promise<void> => {
            log.info(
                'Setting target leg-length %s and offset %s',
                formatLength(adjustments.legLength),
                formatLength(adjustments.offset),
            );
            await executor.executeOperation('set-targets', async (signal) => {
                assert(state.case && state.template);
                // update the template with the new adjustments, so template comparison will work
                state.template.targets.legLength = adjustments.legLength;
                state.template.targets.offset = adjustments.offset;
                await putTemplate(
                    state.case.manualTemplateUrl,
                    syncTemplate(state.template, adjustments),
                    { signal },
                );
                await reloadTemplate(state, { signal });
            });
        },
        approvePlan: async (): Promise<void> => {
            log.info('Approving plan');
            await executor.executeOperation('approve-plan', async (signal) => {
                await approvePlan(state, { signal });
            });
        },
    };
}

/** Stem and head selection / reset mutations. */
export function stemActions(ctx: PlannerContext) {
    const { state, fittedStem, stemResetCount } = ctx;
    return {
        setStem: (url: Url) => {
            assert(state.template && state.catalog);
            if (url === state.template.stemUrl) return;

            const oldFitted = fittedStem.value;
            const oldStemTransform = state.template.stemTransform.clone();
            const hasManualAdjustment = hasStemAdjustment(oldStemTransform);

            const oldCatalog = state.catalog.stems.get(state.template.stemUrl);
            const newCatalog = state.catalog.stems.get(url);

            state.template.stemUrl = url;
            state.template.stemTransform.identity();

            if (hasManualAdjustment && oldFitted && fittedStem.value && oldCatalog && newCatalog) {
                const anchor = stemChangeAnchor(oldCatalog, newCatalog);
                if (anchor) {
                    state.template.stemTransform.copy(
                        computeStemTransformFromResectionAlignment(
                            oldFitted,
                            oldStemTransform,
                            fittedStem.value,
                            anchor,
                        ),
                    );
                }
            }
        },
        resetStem: () => {
            assert(state.template && state.case);
            const autoTemplate = state.case.autoTemplate;
            state.template.stemUrl = autoTemplate.stem;
            state.template.headUrl = autoTemplate.head;
            state.template.stemTransform.identity();
            stemResetCount.value++;
        },
        // Transform-only reset: does not change stemUrl/headUrl, so stemResetCount
        // is intentionally not incremented (that counter signals URL-changing resets).
        resetStemTransform: () => {
            assert(state.template);
            state.template.stemTransform.identity();
        },
        setHead: (url: Url) => {
            assert(state.template);
            state.template.headUrl = url;
        },
    };
}

/** Cup, liner and dual-mobility mutations. */
export function cupActions({ state }: PlannerContext) {
    return {
        setCup: (url: Url) => {
            assert(state.template && state.catalog);
            const catalogCup = state.catalog.cups.get(url);
            if (!catalogCup) {
                throw Error(`Invalid cup url: ${url}`);
            }
            state.template.cupUrl = url;
            state.template.linerUrl = computeLinerUrl(state);
            state.template.bearingUrl = computeBearingUrl(state);
            state.template.headUrl = computeHeadUrl(state);
        },
        resetCup: () => {
            assert(state.case && state.template);
            const autoTemplate = state.case.autoTemplate;
            state.template.cupUrl = autoTemplate.cup;
            state.template.cupRotation = anatomicAngles(toRadians(autoTemplate.cupRotation));
            state.template.cupOffset = autoTemplate.cupOffset;
            state.template.dualMobility = false;

            state.template.linerUrl = autoTemplate.liner;
            state.template.bearingUrl = null;
            // Keep the head offset while resetting the cup
            state.template.headUrl = computeHeadUrl(state);
        },
        setDualMobility: (value: boolean) => {
            assert(state.template);
            state.template.dualMobility = value;
            state.template.linerUrl = computeLinerUrl(state);
            state.template.headUrl = computeHeadUrl(state);
            state.template.bearingUrl = computeBearingUrl(state);
        },
    };
}

/**
 * Catalog-resolved current selection and template-derived flags — the domain siblings of the
 * `fittedCup`/`fittedStem` derivations. Pure functions of `template` + `catalog`, with no scene
 * coupling, so they belong on the Model rather than the scene ViewModel.
 */
export function selectedComponents({ state }: PlannerContext) {
    return {
        selectedCup: computed<CatalogCup | null>(() =>
            state.template ? (state.catalog?.cups.get(state.template.cupUrl) ?? null) : null,
        ),
        dualMobility: computed<boolean>(() => state.template?.dualMobility ?? false),
        hasManualStemTransform: computed<boolean>(
            () => !!state.template && hasStemAdjustment(state.template.stemTransform),
        ),
    };
}

/** Registers the operation-logging and template-sync watchers for the store. */
function registerPlannerWatchers(
    state: PlannerState,
    templateSync: ReturnType<typeof useTemplateSyncStore>,
): void {
    watchImmediate(
        () => state.currentOperation,
        (op) => log.info('Current operation: %s', op ?? 'none'),
    );

    watchImmediate(
        () => state._sync && state.currentOperation === null && state.case && state.template,
        (shouldSync) => {
            if (shouldSync) {
                assert(state.case && state.template);
                templateSync.startSync(state.case.manualTemplateUrl, state.template);
            } else {
                templateSync.stopSync();
            }
        },
    );
}

/**
 * The Model store for the 3D-planning view: raw case/template domain data, API-loaded
 * measurements, the fitting derivations, and domain mutations. It holds **no** scene or
 * presentation state — that lives on `usePlannerSceneViewModel`. See docs/architecture.md →
 * "MVVM for the 3D planner".
 */
export const usePlannerStore: () => PlannerStore = defineStore('planner', () => {
    const templateSync = useTemplateSyncStore();

    const state = reactive<PlannerState>(plannerState());

    const ctx: PlannerContext = {
        state,
        executor: plannerOperationExecutor(toRef(state, 'currentOperation')),
        fittedStem: computed(() => computeFittedStem(state)),
        fittedCup: computed(() => computeFittedCup(state)),
        isLoading: computed(() => state.currentOperation === 'load-case'),
        stemResetCount: ref(0),
    };

    registerPlannerWatchers(state, templateSync);

    return {
        ...toRefs(state),
        fittedStem: ctx.fittedStem,
        fittedCup: ctx.fittedCup,
        isLoading: ctx.isLoading,
        stemResetCount: ctx.stemResetCount,
        ...selectedComponents(ctx),
        ...caseFlowActions(ctx),
        ...stemActions(ctx),
        ...cupActions(ctx),
    };
});
